
import {
    CheckCircleOutlined,
    DescriptionOutlined,
    HighlightOffOutlined,
    HourglassEmptyOutlined,
} from "@mui/icons-material";
import { Box, Button, Chip, Divider, Stack, Typography } from "@mui/material";

import { isRealEnvelopeId } from "../envelope-id";
import { colors, semantic } from "../theme/tokens";

import type { WidgetProps } from "./types";

// v0.5 read-only display of a DocuSign envelope.
//
// Two layers of state on the widget:
//   - props.{envelopeId, signingUrl, status} — the base contract a room JSON
//     always carries. status is the headline indicator.
//   - data.{sentAt, deliveredAt, signedAt, voidedAt, voidReason, recipients,
//     generatedAt} — optional enrichment populated by /api/webhooks/docusign
//     (DocuSign Connect) or by /refresh-deal-room-docusign. When present, we
//     prepend timestamps and per-signer signing progress beneath the headline.
//
// Empty-state rules:
//   - status `pending` → "Document being prepared".
//   - every other status renders its own state. `signingUrl` is NOT required:
//     DocuSign signing URLs are single-use and minted per session, so there is
//     no durable URL to store at send time and the field is in practice always
//     absent. The copy therefore points the signer at their email, which is
//     where DocuSign actually delivers the envelope. If a signingUrl is ever
//     supplied (a future in-room signing session), the button appears on top.
//   - a real (GUID) envelopeId is always shown; placeholders never are.
//
// The card is a status mirror. Signing in the room is SignInRoom, which the
// Agreements pane renders under this card (2026-10-14); the copy names both ways.

type EffectiveStatus = keyof typeof STATUS_META;

export function DocuSignEmbed({ widget }: WidgetProps<"docusign_embed">) {
    const { title, envelopeId, signingUrl, status } = widget.props;
    const data = widget.data ?? null;

    // Resolve the status copy variant we actually want to render. The schema
    // status drives this; anything unrecognised falls back to pending.
    const effectiveStatus: EffectiveStatus = (() => {
        if (status === "deleted") return "voided"; // soft-delete renders same as voided
        if (status in STATUS_META) return status as EffectiveStatus;
        return "pending";
    })();

    const meta = STATUS_META[effectiveStatus];
    const canSign = effectiveStatus === "sent" || effectiveStatus === "delivered";
    const showEnvelopeId = isRealEnvelopeId(envelopeId);

    return (
        <Box
            sx={{
                p: 3,
                border: 1,
                borderColor: "divider",
                borderRadius: "10px",
                bgcolor: "background.paper",
            }}
        >
            <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box sx={{ color: meta.iconColor, mt: 0.5 }}>{meta.icon}</Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        sx={{ mb: 0.5, flexWrap: "wrap" }}
                    >
                        <Typography variant="h6">{title ?? meta.heading}</Typography>
                        <Chip
                            size="small"
                            label={effectiveStatus}
                            sx={{
                                textTransform: "uppercase",
                                fontWeight: 600,
                                fontSize: "0.7rem",
                                bgcolor: meta.chipBg,
                                color: meta.chipColor,
                            }}
                        />
                    </Stack>
                    {title && (
                        <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: meta.iconColor, mb: 0.5 }}
                        >
                            {meta.heading}
                        </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {meta.description}
                    </Typography>
                    {data?.voidReason && (effectiveStatus === "voided") && (
                        <Typography
                            variant="body2"
                            sx={{
                                mb: 2,
                                p: 1.5,
                                bgcolor: meta.chipBg,
                                color: meta.chipColor,
                                borderRadius: 1,
                            }}
                        >
                            <strong>Reason:</strong> {data.voidReason}
                        </Typography>
                    )}
                    {data && <EnvelopeDataBlock data={data} />}
                    {showEnvelopeId && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mb: canSign && signingUrl ? 2 : 0 }}
                        >
                            Envelope ID: {envelopeId}
                        </Typography>
                    )}
                    {canSign && signingUrl && (
                        <Button
                            variant="contained"
                            color="secondary"
                            href={signingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                        >
                            Open signing session
                        </Button>
                    )}
                </Box>
            </Stack>
        </Box>
    );
}

// Render timestamps + per-signer progress when widget.data is populated.
// The headline status copy already covers the overall state; this block adds
// the "who has done what and when" detail that suppliers + SMs actually want.
function EnvelopeDataBlock({
    data,
}: {
    data: NonNullable<WidgetProps<"docusign_embed">["widget"]["data"]>;
}) {
    const events: Array<{ label: string; at: string }> = [];
    if (data.sentAt) events.push({ label: "Sent", at: data.sentAt });
    if (data.deliveredAt) events.push({ label: "First viewed", at: data.deliveredAt });
    if (data.signedAt) events.push({ label: "Completed", at: data.signedAt });
    if (data.voidedAt) events.push({ label: "Voided", at: data.voidedAt });

    if (events.length === 0 && data.recipients.length === 0) return null;

    return (
        <Box sx={{ mb: 2 }}>
            <Divider sx={{ mb: 1.5 }} />
            {events.length > 0 && (
                <Stack
                    direction="row"
                    spacing={2}
                    sx={{ flexWrap: "wrap", rowGap: 0.5, mb: data.recipients.length > 0 ? 1.5 : 0 }}
                >
                    {events.map((e) => (
                        <Typography key={e.label} variant="caption" color="text.secondary">
                            <strong>{e.label}:</strong> {formatTimestamp(e.at)}
                        </Typography>
                    ))}
                </Stack>
            )}
            {data.recipients.length > 0 && (
                <Stack spacing={0.5}>
                    {data.recipients.map((r) => (
                        <Stack
                            key={`${r.email}:${r.role ?? ""}`}
                            direction="row"
                            spacing={1}
                            alignItems="baseline"
                            sx={{ flexWrap: "wrap" }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {r.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {r.role ? `${r.role} · ` : ""}
                                {recipientStatusLabel(r.status)}
                                {r.signedAt ? ` · ${formatTimestamp(r.signedAt)}` : ""}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>
            )}
        </Box>
    );
}

function recipientStatusLabel(status: string): string {
    switch (status) {
        case "completed":
            return "signed";
        case "delivered":
            return "viewed, not signed";
        case "sent":
            return "awaiting signature";
        case "declined":
            return "declined";
        case "created":
            return "not yet sent";
        default:
            return status;
    }
}

function formatTimestamp(iso: string): string {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

const STATUS_META = {
    pending: {
        heading: "Document being prepared",
        description:
            "Your Bison contact is putting the final agreement together. We will notify you as soon as it is ready to sign.",
        icon: <HourglassEmptyOutlined />,
        iconColor: "text.secondary",
        chipBg: colors.lightGray,
        chipColor: colors.darkGray,
    },
    sent: {
        heading: "Ready to sign",
        description:
            "The agreement is out for signature. Signers can sign it here in the room, or from the DocuSign email.",
        icon: <DescriptionOutlined />,
        iconColor: "secondary.main",
        chipBg: semantic.warning.bg,
        chipColor: semantic.warning.text,
    },
    delivered: {
        heading: "Ready to sign",
        description:
            "The agreement has been opened but not yet signed. Signers can finish it here in the room, or from the DocuSign email.",
        icon: <DescriptionOutlined />,
        iconColor: "secondary.main",
        chipBg: semantic.warning.bg,
        chipColor: semantic.warning.text,
    },
    completed: {
        heading: "Signed",
        description:
            "Thank you. The countersigned copy will be available here and emailed to you shortly.",
        icon: <CheckCircleOutlined />,
        iconColor: "primary.main",
        chipBg: semantic.info.bg,
        chipColor: semantic.info.text,
    },
    declined: {
        heading: "Signing declined",
        description:
            "If this was not intended, please reach out to your Bison contact to reissue the envelope.",
        icon: <HighlightOffOutlined />,
        iconColor: semantic.danger.border,
        chipBg: semantic.danger.bg,
        chipColor: semantic.danger.text,
    },
    voided: {
        heading: "Envelope voided",
        description: "This version is no longer valid. A replacement will be issued shortly.",
        icon: <HighlightOffOutlined />,
        iconColor: "text.secondary",
        chipBg: colors.lightGray,
        chipColor: colors.darkGray,
    },
} as const;
